import { useDraggable } from '@dnd-kit/core'
import cn from 'clsx'
import { FC, useState } from 'react'

import { Button, Typography } from '@/components/ui'

import DownloadSimple from '@/assets/icons/download-simple.svg'
import Trash from '@/assets/icons/trash.svg'

import { IDeleteHighlightParams, ISegment } from '@/types/highlight.interface'
import { displayTime } from '@/utils/displayTime'

import DeleteSegmentConfirm from './DeleteSegmentConfirm'

interface IHighlightItemProps {
	segment: ISegment
	index: number
	folderId: number
	deleteHighlight: (
		params: IDeleteHighlightParams
	) => Promise<unknown> | unknown
	downloadSegment: (segment: ISegment) => void
	isSharedView?: boolean
	onScrollToSegment?: (segmentStart: number) => void
}

const HighlightItem: FC<IHighlightItemProps> = ({
	segment,
	index,
	folderId,
	deleteHighlight,
	downloadSegment,
	isSharedView = false,
	onScrollToSegment
}) => {
	const [deleteConfirm, setDeleteConfirm] = useState<{
		x: number
		y: number
	} | null>(null)

	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `segment:${folderId}:${index}`,
		data: {
			type: 'segment',
			sourceFolderId: folderId,
			segmentIndex: index,
			segment
		},
		disabled: isSharedView
	})

	const openDeleteConfirm = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		event.stopPropagation()

		const rect = event.currentTarget.getBoundingClientRect()

		const popupWidth = 397
		const popupHeight = 122
		const gap = 8
		const padding = 8

		const x = Math.min(
			Math.max(rect.right - popupWidth, padding),
			window.innerWidth - popupWidth - padding
		)

		const hasSpaceBelow = window.innerHeight - rect.bottom >= popupHeight + gap

		const y = hasSpaceBelow
			? rect.bottom + gap
			: Math.max(rect.top - popupHeight - gap, padding)

		setDeleteConfirm({ x, y })
	}

	return (
		<div
			ref={setNodeRef}
			className={cn(
				'group/highlight flex justify-between items-center gap-1 overflow-hidden',
				isDragging && 'opacity-50'
			)}
		>
			<div
				className={cn(
					'px-3 py-2 w-full',
					!isSharedView && 'group-hover/highlight:w-[50%]'
				)}
			>
				{/* Timecodes */}
				<Typography
					variant='text'
					className='!text-[12px] text-gray-450 cursor-pointer'
					onPointerDown={event => event.stopPropagation()}
					onClick={event => {
						event.stopPropagation()
						onScrollToSegment?.(segment.start)
					}}
				>
					{displayTime(segment.start)}
					{' - '}
					{displayTime(segment.end)}
				</Typography>

				{/* Highlight text */}
				<Typography
					{...(!isSharedView ? listeners : {})}
					{...(!isSharedView ? attributes : {})}
					variant='text'
					className={cn(
						'w-[70%] text-gray-600 overflow-hidden !whitespace-nowrap truncate select-none',
						!isSharedView && 'cursor-grab'
					)}
				>
					{segment.description}
				</Typography>
			</div>

			{/* Buttons block */}
			{!isSharedView && (
				<div className='flex pr-2.5 pointer-events-none opacity-0 transition-opacity group-hover/highlight:opacity-100 group-hover/highlight:pointer-events-auto'>
					<Button
						className='!p-[6px] h-fit bg-neutral-100 hover:bg-neutral-200 rounded-full'
						onClick={e => {
							e.stopPropagation()
							downloadSegment(segment)
						}}
					>
						<DownloadSimple className='w-4 h-4 text-neutral-400 cursor-pointer' />
					</Button>
					<Button
						className='!p-[6px] h-fit bg-neutral-100 hover:bg-neutral-200 rounded-full'
						onClick={openDeleteConfirm}
					>
						<Trash className='w-4 h-4 text-neutral-400 cursor-pointer' />
					</Button>
				</div>
			)}

			{deleteConfirm && (
				<DeleteSegmentConfirm
					x={deleteConfirm.x}
					y={deleteConfirm.y}
					onCancel={() => setDeleteConfirm(null)}
					onDelete={async () => {
						setDeleteConfirm(null)

						try {
							await deleteHighlight({ highlightId: folderId, index })
						} catch (error) {
							console.log(error)
						}
					}}
				/>
			)}
		</div>
	)
}

export default HighlightItem
